import type { FC, ReactNode } from 'react'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { cn, slugify } from '@/lib/utils'
import { ARTICLE_PROSE } from '@/lib/article-styles'
import { injectHeadingIds, isHtmlContent } from '@/lib/toc'

function nodeToString(node: ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') return ''
    if (typeof node === 'string' || typeof node === 'number') return String(node)
    if (Array.isArray(node)) return node.map(nodeToString).join('')

    const element = node as { props?: { children?: ReactNode } }

    if (element.props) return nodeToString(element.props.children)

    return ''
}

interface ArticleBodyProps {
    content: string
    className?: string
}

export const ArticleBody: FC<ArticleBodyProps> = ({ content, className }): ReactNode => {
    // Posts saved from the WYSIWYG editor are HTML; older posts are Markdown.
    if (isHtmlContent(content)) {
        return (
            <div
                className={cn(ARTICLE_PROSE, className)}
                dangerouslySetInnerHTML={{ __html: injectHeadingIds(content) }}
            />
        )
    }

    return (
        <div className={cn(ARTICLE_PROSE, className)}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h2: ({ children }) => (
                        <h2 id={slugify(nodeToString(children))} className='scroll-mt-28'>
                            {children}
                        </h2>
                    )
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    )
}
